import { useContext, useMemo } from 'react';

import { DateTime } from 'luxon';
import { TiArrowRightThick } from 'react-icons/ti';

import { Group } from './GroupAvailability';
import { GroupSpacesRemaining } from './GroupSpacesRemaining';
import PriceFormat from '@website/components/utilities/PriceFormat';
import { Store } from '@website/Store';
import { FetchUsersWaitListQuery } from '@website/types/graphqlOperations';



interface GroupAvailabilityProps {
  group: Group;
  userWaitList: FetchUsersWaitListQuery['user']['waitList']['items'];
}

export function GroupAvailabilityRow({
  group,
  userWaitList,
}: GroupAvailabilityProps) {
  const { state } = useContext(Store);

  // Calculate show deposit
  // Note - doesn't use start date's timezone, but fine for now as 69 days is approx
  // (we want 2 months + 7 days ish)
  const showDeposit = useMemo(() => {
    const start = DateTime.fromISO(group.start.utc);
    const now = DateTime.now().setZone('utc');
    const diff = start.diff(now, 'days');
    if (diff.days < 69) {
      return false;
    }
    return true;
  }, [group.start]);

  // Get the start and end date
  const start = DateTime.fromISO(group.start.utc, {
    zone: group.start.zone,
  });
  const end = DateTime.fromISO(group.end.utc, {
    zone: group.end.zone,
  });

  const discountRate = useMemo(() => {
    if (group.guestPriceBeforeDiscount && group.remainingSpaces > 0) {
      return (
        // we calculate the discount rate
        Math.round(
          (1 -
            group.guestPrice.amountZD /
              group.guestPriceBeforeDiscount.amountZD) *
            100,
        )
      );
    }
    return undefined;
  }, [
    group.guestPrice.amountZD,
    group.guestPriceBeforeDiscount,
    group.remainingSpaces,
  ]);

  // Add in based on
  const basedOn = useMemo(() => {
    if (
      state.preferred_currency !== group.guestPrice.currency &&
      group.remainingSpaces > 0
    ) {
      return (
        <p className="price-based-on">
          <em>
            Based on{' '}
            <PriceFormat
              amount={group.guestPrice.amountZD}
              currencyIso={group.guestPrice.currency}
            />
          </em>
        </p>
      );
    }
    return null;
  }, [
    state.preferred_currency,
    group.guestPrice.currency,
    group.guestPrice.amountZD,
    group.remainingSpaces,
  ]);

  return (
    <tr
      key={group.id + group.start.utc}
      className={group.remainingSpaces === 0 ? 'group-spaces-0' : ''}
      data-cy="availability-date"
    >
      <td className="trip-date">
        <div className="trip-date-inner">
          <div className="clearfix">
            <div className="start-date">
              <div className="day">{start.toFormat('ccc')}</div>
              <div className="date">{start.toFormat('d LLL')}</div>
              <div className="time">{start.toFormat('h:mma')}</div>
            </div>
            <span className="date-arrow">
              <TiArrowRightThick />
            </span>
            <div className="end-date">
              <div className="day">{end.toFormat('ccc')}</div>
              <div className="date">{end.toFormat('d LLL')}</div>
              <div className="time">{end.toFormat('h:mma')}</div>
            </div>
          </div>
        </div>
      </td>
      <td className="trip-price">
        {group.guestPriceBeforeDiscount && group.remainingSpaces > 0 && (
          <div className="block text-sm fw-bold">
            <span
              className="inline-block text-decoration-line-through d-block d-md-inline"
              style={{
                maxWidth: 'max-content',
              }}
            >
              <PriceFormat
                amount={group.guestPriceBeforeDiscount.amountZD}
                currencyIso={group.guestPriceBeforeDiscount.currency}
                convert
                roundDollar
                short
              />
            </span>
          </div>
        )}
        <div
          className={
            group.guestPriceBeforeDiscount && group.remainingSpaces > 0
              ? 'text-secondary block'
              : 'block'
          }
        >
          <PriceFormat
            amount={group.guestPrice.amountZD}
            currencyIso={group.guestPrice.currency}
            convert
            short
          />

          {discountRate && (
            <span className="fst-italic text-sm d-none d-md-inline">
              {' '}
              ({discountRate}% off)
            </span>
          )}
        </div>
        {showDeposit && group.remainingSpaces > 0 && (
          <p className="price-deposit">
            (
            <PriceFormat
              amount={group.guestDeposit.amountZD}
              currencyIso={group.guestPrice.currency}
              short
              convert
            />{' '}
            deposit)
          </p>
        )}
        {basedOn}
      </td>
      <td className="trip-quantity">
        <GroupSpacesRemaining
          remainingSpaces={group.remainingSpaces}
          groupID={group.id}
          userWaitList={userWaitList}
        />
      </td>
    </tr>
  );
}
